/* ═══════════════════════════════════════════════════════════════════════════
   Homepage — Static Data (v2: Capuchin-centric)
   ═══════════════════════════════════════════════════════════════════════════ */

/* ── Navigation ──────────────────────────────────────────── */

export interface NavItem {
  href: string;
  label: string;
  /** If true, this item is part of a dropdown group */
  dropdown?: NavItem[];
}

/** Primary nav items visible in the header bar */
export const NAV_ITEMS: NavItem[] = [
  { href: "/features", label: "Features" },
  { href: "/code-agent", label: "Code Agent" },
  {
    href: "/models",
    label: "Models",
    dropdown: [
      { href: "/auto-mode", label: "Auto Mode" },
      { href: "/capuchin", label: "Capuchin" },
      { href: "/#models", label: "All models" },
    ],
  },
  { href: "/pricing", label: "Pricing" },
  { href: "/docs", label: "Docs" },
  { href: "/for-teams", label: "For Teams" },
  { href: "/promo", label: "Start Free" },
];

/* Old NAV_LINKS kept for backward compat in older components */
export const NAV_LINKS = NAV_ITEMS;

export const HERO_CHIPS = [
  { label: "Capuchin Free", sub: "try it free — no card", icon: "zap" },
  { label: "Frontier In-Plan", sub: "Claude · Gemini · ChatGPT allowance", icon: "globe" },
  { label: "Auto Mode", sub: "the right model for every step", icon: "sparkles" },
  { label: "Also 100% Local", sub: "or bring your own key", icon: "shield" },
];

export const PILLAR_CARDS = [
  {
    num: "01",
    title: "Capuchin, free to start.",
    body: "Our fast-and-reasoning coding model ships with the IDE — try it free with the promo, no card. Capuchin Flash for instant edits, Capuchin Reason for planning and agents. Unlimited on every paid plan.",
    accent: "var(--brand-teal)",
  },
  {
    num: "02",
    title: "The frontier, on one subscription.",
    body: "Claude, Gemini, and ChatGPT included as a transparent monthly allowance, and it goes further here. Auto mode spends frontier tokens only where they change the outcome, and lean local context averages ~$0.06 per frontier request.",
    accent: "var(--info)",
  },
  {
    num: "03",
    title: "Or run it entirely yours.",
    body: "The full IDE also works 100% locally or with your own API key — free forever. On-device indexing, air-gapped mode, signed and replayable agent runs. No lock-in, ever.",
    accent: "var(--success)",
  },
];

/* ── Models Band ─────────────────────────────────────────── */

export const MODEL_CARDS = [
  {
    key: "capuchin",
    title: "Capuchin",
    tag: "try it free",
    body: "Our fast-and-reasoning coding model — free to try with the promo, unlimited on every paid plan. Capuchin Flash for instant edits, Capuchin Reason for planning and agents. No per-token surprise.",
    accent: "#10b981",
    icon: "zap",
  },
  {
    key: "commercial",
    title: "Claude · Gemini · ChatGPT",
    tag: "in your plan",
    body: "Included as a monthly allowance on paid plans — one subscription instead of three. Lean local context keeps requests at ~$0.06 on average, so your allowance stretches to hundreds of frontier agent runs. BYOK also supported.",
    accent: "#1a73e8",
    icon: "globe",
  },
  {
    key: "local",
    title: "Your own — local",
    tag: "free forever",
    body: "Run Ollama, llama.cpp, or vLLM on your hardware. Zero data leaves the machine — and the IDE stays free.",
    accent: "#8b5cf6",
    icon: "laptop",
  },
  {
    key: "remote",
    title: "Your own — remote",
    tag: "your infra",
    body: "Point MonkeysCode at your own server or any OpenAI-compatible endpoint. Your infrastructure, your rules.",
    accent: "#f59e0b",
    icon: "link",
  },
];

export const MODEL_CHIPS = ["Capuchin", "Claude", "Gemini", "ChatGPT", "Local", "Your Server", "BYOK"];

/* ── Contrast Table ──────────────────────────────────────── */

export const CONTRAST_ROWS = [
  { left: "Paying Claude + ChatGPT + Gemini separately", right: "One plan: unlimited Capuchin + a frontier allowance that covers all three", icon: "credit-card" },
  { left: "Whole files stuffed into every prompt — tokens burned", right: "On-device context engine sends only what's relevant: ~$0.06 avg per frontier request, ~90% cached context", icon: "search" },
  { left: "Opaque token burn, surprise invoices", right: "Live consumption dashboard, per-task budgets, hard cap", icon: "plug" },
  { left: "Picking a model by hand, then hitting a limit wall mid-task", right: "Auto mode picks per step and keeps going on Capuchin if the frontier pool runs out", icon: "refresh" },
  { left: "One vendor's model only (Claude Code, Codex)", right: "Capuchin, Claude, Gemini, ChatGPT — or your own, switchable per task", icon: "lock" },
  { left: "Your codebase indexed in their cloud", right: "Indexed on your machine — air-gapped if needed", icon: "cloud" },
  { left: "Extensions tied to one marketplace", right: "Open VSX + sideload + self-hostable registry", icon: "puzzle" },
];

/* ── Feature Highlights ──────────────────────────────────── */

export const FEATURES = [
  {
    id: "feature-models", eyebrow: "Provider-neutral", image: "/feature-models.png",
    heading: "Capuchin, the big three, or your own.",
    bullets: [
      { bold: "Capuchin — our model", body: "Fast-and-reasoning, two tiers (Flash + Reason), tuned for coding and included in your plan." },
      { bold: "Claude · Gemini · ChatGPT", body: "First-class via BYOK or managed; swap with a setting, not a reinstall." },
      { bold: "Your own, local or remote", body: "Ollama/llama.cpp/vLLM on your machine, or your own server / OpenAI-compatible endpoint. Same agent features either way." },
      { bold: "No degraded fallback", body: "Local and self-hosted are full tiers, not a lesser mode." },
    ],
    chips: ["Capuchin", "Claude", "Gemini", "ChatGPT", "Local", "Your Server"],
  },
  {
    id: "local", eyebrow: "Local-first", image: "/feature-local.png",
    heading: "Your codebase never has to leave your machine.",
    bullets: [
      { bold: "On-device index", body: "Merkle-tracked, tree-sitter-chunked, embedded and searched locally." },
      { bold: "Air-gapped mode", body: "Local index + local models + internal registry. Zero outbound calls." },
      { bold: "Optional team sync", body: "Share an encrypted index when you want; turn it off and you're fully local." },
      { bold: "Monorepo-aware", body: "A symbol/dependency graph so agents reason across services, not just nearby files." },
    ],
    chips: ["On-device", "Air-gap ready", "Encrypted sync", "Monorepo graph"],
  },
  {
    id: "trust", eyebrow: "Verifiable by design", image: "/feature-trust.png",
    heading: "Every agent run, signed and replayable.",
    bullets: [
      { bold: "Signed event logs", body: "A tamper-evident, replayable trail of plan, tools, and diffs." },
      { bold: "Test-gated apply", body: "Agents can't commit code that fails your tests." },
      { bold: "Content-signed diffs", body: "Verify exactly what changed and reproduce it byte for byte." },
      { bold: "Audit-ready", body: "Hand a run to a reviewer or compliance officer to check themselves." },
    ],
    chips: ["Signed runs", "Replay", "Test-gated", "Reproducible"],
  },
  {
    id: "cost", eyebrow: "Every token pulls its weight", image: "/feature-cost.png",
    heading: "Your frontier allowance goes further here.",
    bullets: [
      { bold: "Auto mode spends where it counts", body: "Capuchin runs the loop, unlimited on paid plans. A frontier model steps in only when tests fail or the agent gets stuck, and every step shows which model ran it." },
      { bold: "Lean context, real savings", body: "The on-device context engine sends only relevant code slices: ~$0.06 avg per frontier request on the priciest model, ~90% of context served from cache." },
      { bold: "Per-task budgets + hard cap", body: "Agents stop instead of surprising you. Live consumption dashboard included." },
      { bold: "BYOK if you prefer", body: "Use Claude/Gemini/ChatGPT on your own key; we don't mark up your tokens." },
    ],
    chips: ["Included Capuchin", "BYOK", "Budgets", "Hard cap"],
  },
  {
    id: "agents", eyebrow: "Agentic, not just autocomplete", image: "/feature-agents.png",
    heading: "Plan. Edit across files. Verify. In parallel.",
    bullets: [
      { bold: "Plan or Fast", body: "Approve a plan for big changes, or fire instant edits for small ones." },
      { bold: "Parallel agents", body: "Split work across files and services, then merge cleanly." },
      { bold: "Real tools, gated", body: "Terminal, git, test runner, browser, MCP — each behind permissions." },
      { bold: "Browser verification", body: "Agents prove a web change works with real navigation and screenshots." },
    ],
    chips: ["Plan mode", "Parallel", "MCP", "Browser verify"],
  },
];

/* ── Roadmap Section ─────────────────────────────────────── */

export const ROADMAP_PHASES = [
  {
    status: "now" as const,
    label: "Now",
    title: "MonkeysCode IDE",
    platforms: "Win · macOS · Linux",
    body: "The full agentic editor. Goes head-to-head with Cursor and Antigravity.",
    accent: "var(--success)",
  },
  {
    status: "now" as const,
    label: "Now",
    title: "Code Agent",
    platforms: "Win · macOS · Linux",
    body: "A desktop app to launch, watch, and orchestrate parallel agents — without the full editor. Same engine as the IDE. Included in every plan.",
    accent: "var(--info)",
  },
  {
    status: "now" as const,
    label: "Now",
    title: "MonkeysCode CLI",
    platforms: "Win · macOS · Linux · CI",
    body: "Agentic coding from the terminal and CI, headless, with the same engine. Runs Auto mode, Capuchin, Claude, Gemini, ChatGPT, or your own model. Included in every plan.",
    callout: "Claude Code only runs Claude; Codex only runs GPT — the MonkeysCode CLI runs all of them.",
    accent: "var(--brand-teal)",
  },
];

/* ── Compare Strip ───────────────────────────────────────── */

export type CompareValue = "yes" | "partial" | "no" | string;

export interface CompareRow {
  feature: string;
  cursor: CompareValue;
  antigravity: CompareValue;
  claudeCode: CompareValue;
  codex: CompareValue;
  monkeysCode: CompareValue;
}

export const COMPARE_HEADERS = ["", "Cursor", "Antigravity", "Claude Code", "Codex", "MonkeysCode"];

export const COMPARE_ROWS: CompareRow[] = [
  {
    feature: "Surface",
    cursor: "IDE",
    antigravity: "IDE + agent app",
    claudeCode: "Terminal",
    codex: "Terminal / cloud",
    monkeysCode: "IDE · Code Agent · CLI",
  },
  {
    feature: "Models",
    cursor: "own + commercial",
    antigravity: "Gemini-first",
    claudeCode: "Claude only",
    codex: "GPT only",
    monkeysCode: "Capuchin + Claude + Gemini + ChatGPT, routed by Auto mode",
  },
  {
    feature: "Your own / local model",
    cursor: "no",
    antigravity: "no",
    claudeCode: "no",
    codex: "no",
    monkeysCode: "yes",
  },
  {
    feature: "Fully local / air-gapped",
    cursor: "no",
    antigravity: "no",
    claudeCode: "no",
    codex: "no",
    monkeysCode: "yes",
  },
  {
    feature: "Signed, verifiable runs",
    cursor: "partial",
    antigravity: "partial",
    claudeCode: "no",
    codex: "no",
    monkeysCode: "yes",
  },
  {
    feature: "Self-host the stack",
    cursor: "no",
    antigravity: "no",
    claudeCode: "no",
    codex: "no",
    monkeysCode: "yes",
  },
  {
    feature: "Checkpoints & revert",
    cursor: "yes",
    antigravity: "partial",
    claudeCode: "yes",
    codex: "partial",
    monkeysCode: "yes",
  },
  {
    feature: "Subagents",
    cursor: "yes",
    antigravity: "partial",
    claudeCode: "yes",
    codex: "yes",
    monkeysCode: "yes",
  },
  {
    feature: "Hooks & event triggers",
    cursor: "no",
    antigravity: "no",
    claudeCode: "yes",
    codex: "no",
    monkeysCode: "yes",
  },
  {
    feature: "SSH execution",
    cursor: "no",
    antigravity: "no",
    claudeCode: "no",
    codex: "no",
    monkeysCode: "yes",
  },
  {
    feature: "Test-gated apply",
    cursor: "no",
    antigravity: "no",
    claudeCode: "no",
    codex: "no",
    monkeysCode: "yes",
  },
  {
    feature: "Bounded cost",
    cursor: "Metered",
    antigravity: "Metered",
    claudeCode: "Metered",
    codex: "Metered",
    monkeysCode: "Quota",
  },
];

/* ── Ecosystem Section ───────────────────────────────────── */

export const ECOSYSTEM_FLOW = [
  { name: "MonkeysCode", verb: "write", color: "var(--brand-teal)", active: true },
  { name: "MonkeysLegion", verb: "build", color: "var(--info)", active: false },
];

export const ECOSYSTEM_CARDS = [
  { icon: "zap", title: "First-class for MonkeysLegion", body: "Capuchin understands PHP 8.4 and the 28-package framework, not just JavaScript." },
  { icon: "brain", title: "Powered by Apex", body: "The same orchestration engine in the framework drives the IDE's agents." },
  { icon: "shield", title: "Signed, replayable runs", body: "Every agent action is verifiable — hand a run to a teammate or an auditor." },
  { icon: "lock", title: "No lock-in", body: "Open core, Open VSX, MIT framework underneath. Your code is yours." },
];

/* ── Segments Section ────────────────────────────────────── */

export const SEGMENTS = [
  { icon: "banknote", title: "One subscription, every model", body: "Unlimited Capuchin + Claude, Gemini, ChatGPT in one plan — instead of three separate bills.", tag: "Value" },
  { icon: "landmark", title: "Cost-efficient frontier usage", body: "~$0.06 avg per frontier request; budgets and a hard cap keep spend predictable.", tag: "Economics" },
  { icon: "wrench", title: "Polyglot & back-end shops", body: "First-class PHP, Python, Go, Rust.", tag: "Languages" },
  { icon: "git-branch", title: "Open-source-minded developers", body: "Open core, Open VSX, no lock-in.", tag: "Freedom" },
  { icon: "cloud", title: "Built for teams", body: "One subscription from first keystroke to production, with shared billing and usage.", tag: "Teams" },
];

/* ── Trust Band ──────────────────────────────────────────── */

export const TRUST_ITEMS = [
  { icon: "unlock", title: "Capuchin free to try", body: "Claim the promo and code with our model — no card required.", stat: "$0", sub: "To get started" },
  { icon: "signature", title: "Efficient frontier usage", body: "Lean local context and caching keep frontier requests cheap.", stat: "~$0.06", sub: "Avg per request" },
  { icon: "door", title: "No lock-in", body: "100% local or BYOK — your models, your keys, your machine.", stat: "Zero", sub: "Vendor lock-in" },
];

/* ── Footer ──────────────────────────────────────────────── */

export const FOOTER_COLS = [
  { title: "Product", links: [
    { label: "Features", href: "/features" }, { label: "Code Agent", href: "/code-agent" },
    { label: "Auto Mode", href: "/auto-mode" }, { label: "Capuchin", href: "/capuchin" },
    { label: "Models", href: "/#models" }, { label: "Self-Host", href: "/#selfhost" },
    { label: "Roadmap", href: "/#roadmap" }, { label: "Download", href: "/download" },
    { label: "Changelog", href: "/changelog" },
  ]},
  { title: "Ecosystem", links: [
    { label: "MonkeysLegion", href: "https://monkeyslegion.com" },
  ]},
  { title: "Resources", links: [
    { label: "Docs", href: "/docs" }, { label: "GitHub", href: "https://github.com/MonkeysCodeAI" },
    { label: "Status", href: "https://status.monkeyscode.com" },    ]},
  { title: "Company", links: [
    { label: "About", href: "/about" }, { label: "For Teams", href: "/for-teams" },
    { label: "Contact", href: "/contact" }, { label: "Privacy", href: "/privacy" },
    { label: "Terms", href: "/terms" }, { label: "Security", href: "/security" },
    { label: "Support", href: "/support" },
  ]},
];

export const SOCIALS = [
  { label: "LinkedIn", href: "https://www.linkedin.com/company/monkeyscode" },
  { label: "X", href: "https://x.com/monkeyscodeai" },
  { label: "YouTube", href: "https://www.youtube.com/@MonkeysCode" },
  { label: "Discord", href: "https://discord.gg/bc67NJJXPY" },
  { label: "Instagram", href: "https://www.instagram.com/monkeyscodeai/" },
  { label: "Bluesky", href: "https://bsky.app/profile/monkeyscode.bsky.social" },
  { label: "Threads", href: "https://www.threads.com/@monkeyscodeai" },
  { label: "GitHub", href: "https://github.com/MonkeysCodeAI" },
];
